import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import axios from "../../../utils/api-client"
import {
	PlantViewQuestion,
	PlantViewResponse,
	PlantViewState,
} from "./plant-view-types"

const API = process.env.REACT_APP_API_END_POINT

const initialState: PlantViewState = {
	locationId: null,
	plant: { status: "idle", data: null, error: "" },
	formQuestions: { status: "idle", formId: null, data: [], error: "" },
}

export const fetchPlantView = createAsyncThunk(
	"plantView/fetchPlantView",
	async (locationId: number | undefined, { fulfillWithValue, rejectWithValue }) => {
		try {
			// locationId optional — backend resolves the plant-view location from
			// the authenticated user's company when omitted.
			const qs = locationId ? `?locationId=${locationId}` : ""
			const res = await axios.get(
				`${API}/PlantView/GetPlantView${qs}`
			)
			return fulfillWithValue(res.data as PlantViewResponse)
		} catch (err: any) {
			return rejectWithValue(err?.response?.data?.message ?? "Failed to load plant view")
		}
	}
)

export const fetchFormQuestions = createAsyncThunk(
	"plantView/fetchFormQuestions",
	async (formId: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get(
				`${API}/PlantView/GetFormQuestions?formId=${formId}`
			)
			return fulfillWithValue({ formId, questions: res.data as PlantViewQuestion[] })
		} catch (err: any) {
			return rejectWithValue(err?.response?.data?.message ?? "Failed to load questions")
		}
	}
)

const plantViewSlice = createSlice({
	name: "plantView",
	initialState,
	reducers: {
		setPlantLocation(state, action: { payload: number }) {
			state.locationId = action.payload
		},
		clearFormQuestions(state) {
			state.formQuestions = { status: "idle", formId: null, data: [], error: "" }
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchPlantView.pending, (state) => {
				state.plant.status = "pending"
				state.plant.error = ""
			})
			.addCase(fetchPlantView.fulfilled, (state, action) => {
				state.plant.status = "fulfilled"
				state.plant.data = action.payload as PlantViewResponse
			})
			.addCase(fetchPlantView.rejected, (state, action) => {
				state.plant.status = "rejected"
				state.plant.error = (action.payload as string) ?? "Failed to load plant view"
			})
			.addCase(fetchFormQuestions.pending, (state, action) => {
				state.formQuestions.status = "pending"
				state.formQuestions.formId = action.meta.arg
				state.formQuestions.data = []
				state.formQuestions.error = ""
			})
			.addCase(fetchFormQuestions.fulfilled, (state, action: any) => {
				state.formQuestions.status = "fulfilled"
				state.formQuestions.formId = action.payload.formId
				state.formQuestions.data = action.payload.questions
			})
			.addCase(fetchFormQuestions.rejected, (state, action) => {
				state.formQuestions.status = "rejected"
				state.formQuestions.error = (action.payload as string) ?? "Failed to load questions"
			})
	},
})

export const { setPlantLocation, clearFormQuestions } = plantViewSlice.actions
export default plantViewSlice.reducer
